import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { toast } from "react-toastify";
import { useIntl } from "react-intl";
import { useBoardCore } from "../../../../cores/boards";
import { BoardType } from "../../../../cores/boards/constants/board-type.constant";
import { BoardList } from "../../../../cores/boards/interfaces/api/get-board";
import { Contact } from "../../../../cores/contacts/interfaces/api/get-contacts";
import { useMeCore } from "../../../../cores/me";
import { InputWembii } from "../../InputWembii";
import {
  contactHasOpportunityOnBoard,
  isDuplicateOpportunityError,
} from "../../forms/facebook/leadContact.utils";

interface Props {
  contacts: Contact[];
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const DEFAULT_BOARD_NAME = "Pipeline comercial";
const DEFAULT_LIST_NAME = "Nuevas oportunidades";
const WEMBII_BLUE = "#0B65C2";

const getOpportunityTitle = (contact: Contact) =>
  `Oportunidad de ${contact.name || contact.email || contact.phoneNumber || "contacto"}`;

export default function BulkCreateOpportunitiesModal({
  contacts,
  isOpen,
  onClose,
  onSuccess,
}: Props) {
  const intl = useIntl();
  const { company } = useMeCore();
  const {
    boards,
    getBusinessBoards,
    getLists,
    postBoard,
    postList,
    postCard,
  } = useBoardCore();
  const [boardId, setBoardId] = useState("");
  const [listId, setListId] = useState("");
  const [availableLists, setAvailableLists] = useState<BoardList[]>([]);
  const [createNewBoard, setCreateNewBoard] = useState(false);
  const [boardName, setBoardName] = useState(DEFAULT_BOARD_NAME);
  const [listName, setListName] = useState(DEFAULT_LIST_NAME);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const businessBoards = useMemo(
    () => boards.filter((board) => board.type === BoardType.BUSINESS),
    [boards]
  );

  useEffect(() => {
    if (isOpen && company?.id) {
      getBusinessBoards(company.id);
    }
  }, [company?.id, getBusinessBoards, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setBoardId("");
      setListId("");
      setAvailableLists([]);
      setCreateNewBoard(false);
      setBoardName(DEFAULT_BOARD_NAME);
      setListName(DEFAULT_LIST_NAME);
      setIsSubmitting(false);
      return;
    }

    if (businessBoards.length === 0) {
      setCreateNewBoard(true);
      return;
    }

    if (!boardId) {
      setBoardId(businessBoards[0].id);
    }
  }, [boardId, businessBoards, isOpen]);

  useEffect(() => {
    if (!isOpen || !company?.id || !boardId || createNewBoard) {
      setAvailableLists([]);
      return;
    }

    let cancelled = false;
    setListId("");
    setAvailableLists([]);

    getLists(company.id, boardId).then((lists = []) => {
      if (cancelled) return;
      setAvailableLists(lists);
      setListId(lists[0]?.id || "");
    });

    return () => {
      cancelled = true;
    };
  }, [boardId, company?.id, createNewBoard, getLists, isOpen]);

  const handleBoardChange = useCallback((nextBoardId: string) => {
    setBoardId(nextBoardId);
    setListId("");
  }, []);

  const canSubmit = useMemo(() => {
    if (isSubmitting || contacts.length === 0) return false;
    if (createNewBoard) return !!boardName.trim() && !!listName.trim();
    return !!boardId && !!listId;
  }, [boardId, boardName, contacts.length, createNewBoard, isSubmitting, listId, listName]);

  const handleSubmit = useCallback(async () => {
    if (!company?.id || !canSubmit) return;

    setIsSubmitting(true);
    try {
      let targetBoardId = boardId;
      let targetListId = listId;
      const boardOpportunityCache = new Map<string, boolean>();

      if (createNewBoard) {
        const createdBoard = await postBoard(
          company.id,
          boardName.trim(),
          WEMBII_BLUE,
          "CLP",
          BoardType.BUSINESS,
          []
        );

        if (!createdBoard?.id) {
          throw new Error("No se pudo crear el panel CRM");
        }

        const createdList = await postList(
          company.id,
          listName.trim(),
          createdBoard.id
        );

        if (!createdList?.id) {
          throw new Error("No se pudo crear la columna del panel");
        }

        targetBoardId = createdBoard.id;
        targetListId = createdList.id;
      }

      const results: Array<{ contact: Contact; created: boolean; skipped: boolean }> = [];

      for (const contact of contacts) {
        if (!createNewBoard) {
          const alreadyExists = await contactHasOpportunityOnBoard(
            company.id,
            contact.id,
            targetBoardId,
            boardOpportunityCache
          );

          if (alreadyExists) {
            results.push({
              contact,
              created: false,
              skipped: true,
            });
            continue;
          }
        }

        try {
          const card = await postCard(
            company.id,
            targetListId,
            getOpportunityTitle(contact),
            targetBoardId,
            [contact.id]
          );

          const created = Boolean(card?.id);
          if (created) {
            boardOpportunityCache.set(`${contact.id}:${targetBoardId}`, true);
          }

          results.push({
            contact,
            created,
            skipped: false,
          });
        } catch (error) {
          if (isDuplicateOpportunityError(error)) {
            boardOpportunityCache.set(`${contact.id}:${targetBoardId}`, true);
            results.push({
              contact,
              created: false,
              skipped: true,
            });
            continue;
          }

          results.push({
            contact,
            created: false,
            skipped: false,
          });
        }
      }

      const createdCount = results.filter((result) => result.created).length;
      const skippedCount = results.filter((result) => result.skipped).length;
      const failedCount = results.length - createdCount - skippedCount;

      if (createdCount === 0 && skippedCount === 0) {
        throw new Error("No se creó ninguna oportunidad");
      }

      await getBusinessBoards(company.id);
      if (failedCount > 0) {
        toast.warning(
          `${createdCount} oportunidad(es) creada(s), ${skippedCount} omitida(s) por duplicado y ${failedCount} fallida(s).`
        );
      } else if (createdCount === 0 && skippedCount > 0) {
        toast.info(
          `${skippedCount} contacto(s) ya tenían una oportunidad en este panel. No se crearon duplicados.`
        );
      } else if (skippedCount > 0) {
        toast.success(
          `${createdCount} oportunidad(es) creada(s). ${skippedCount} omitida(s) porque ya existían en este panel.`
        );
      } else {
        toast.success(`${createdCount} oportunidades creadas en CRM`);
      }
      if (failedCount === 0) onSuccess?.();
      onClose();
    } catch (error) {
      console.error(error);
      toast.error("No se pudieron crear las oportunidades en CRM");
    } finally {
      setIsSubmitting(false);
    }
  }, [
    boardId,
    boardName,
    canSubmit,
    company?.id,
    contacts,
    createNewBoard,
    getBusinessBoards,
    listId,
    listName,
    onClose,
    onSuccess,
    postBoard,
    postCard,
    postList,
  ]);

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered>
      <ModalHeader toggle={onClose}>Crear oportunidad en CRM</ModalHeader>
      <ModalBody>
        <p className="text-muted mb-3">
          Se crearán oportunidades para {contacts.length} contacto(s)
          seleccionado(s).
        </p>

        {businessBoards.length > 0 && (
          <div className="mb-3">
            <Button
              type="button"
              color={createNewBoard ? "secondary" : "primary"}
              outline={createNewBoard}
              className="mr-2"
              onClick={() => setCreateNewBoard(false)}
            >
              Usar panel existente
            </Button>
            <Button
              type="button"
              color={createNewBoard ? "primary" : "secondary"}
              outline={!createNewBoard}
              onClick={() => setCreateNewBoard(true)}
            >
              Crear nuevo panel
            </Button>
          </div>
        )}

        {createNewBoard ? (
          <>
            {businessBoards.length === 0 && (
              <p className="text-warning mb-3">
                No hay paneles CRM disponibles. Se creará uno automáticamente.
              </p>
            )}
            <InputWembii
              type="text"
              label="Nombre del panel CRM"
              value={boardName}
              onChange={setBoardName}
            />
            <InputWembii
              type="text"
              label="Nombre de la columna"
              value={listName}
              onChange={setListName}
            />
          </>
        ) : (
          <>
            <InputWembii
              type="select"
              label={intl.formatMessage({ id: "app.sidebar.crm.business" })}
              value={boardId}
              onChange={handleBoardChange}
            >
              {businessBoards.map((board) => (
                <option key={board.id} value={board.id}>
                  {board.name}
                </option>
              ))}
            </InputWembii>
            <InputWembii
              type="select"
              label={intl.formatMessage({ id: "app.crmBusiness.list" })}
              value={listId}
              onChange={setListId}
            >
              {availableLists.map((list) => (
                <option key={list.id} value={list.id}>
                  {list.name}
                </option>
              ))}
            </InputWembii>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" outline onClick={onClose} disabled={isSubmitting}>
          {intl.formatMessage({ id: "app.socialMediaPost.cancel" })}
        </Button>
        <Button color="primary" onClick={handleSubmit} disabled={!canSubmit}>
          {isSubmitting ? "Creando..." : "Crear oportunidades"}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
